import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable, tap } from "rxjs";
import { LoaderService } from "./loader.service";
import { ActionResult, ActionResultList, CouponUserRequest, Discount, EventItem, UserDetail } from "@model";
import { EnvironmentService } from "./environment.service";

@Injectable({ providedIn: 'root' })
export class DiscountsService {

  constructor(private http: HttpClient, private loaderService: LoaderService, private environmentService: EnvironmentService) {}

  findDiscounts(organiserId?: number, hideLoading?: boolean): Observable<Discount[]> {
    if (!hideLoading) this.loaderService.startLoading();
    return this.http.get<ActionResultList<Discount[], number>>(
      this.environmentService.getApiUrl() + "/api/discounts/find" + (organiserId ? "?organiserId=" + organiserId : "")
    ).pipe(
      tap(() => { if (!hideLoading) this.loaderService.dismissLoading(); }),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  fetchDiscount(discountId: number, organiserId?: number): Observable<Discount> {
    this.loaderService.startLoading();
    return this.http.get<ActionResult<Discount>>(
      this.environmentService.getApiUrl() + "/api/discounts/find/discount?discountId=" + discountId + (organiserId ? "&organiserId=" + organiserId : "")
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value?.result ?? new Discount())
    );
  }

  updateDiscount(discount: Discount, onSuccess: (discount: Discount) => void, onError: () => void, organiserId?: number) {
    this.loaderService.startLoading();
    this.http.post<ActionResult<Discount>>(
      this.environmentService.getApiUrl() + (discount.id ? "/api/discounts/update" : "/api/discounts/add"), discount
    ).pipe(
      tap(() => this.loaderService.dismissLoading())
    ).subscribe(value => value?.result ? onSuccess(value.result) : onError());
  }

  deleteDiscount(discount: Discount, organiserId?: number): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.delete<ActionResult<boolean>>(
      this.environmentService.getApiUrl() + "/api/discounts/delete?discountId=" + discount.id + (organiserId ? "&organiserId=" + organiserId : "")
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value.result ?? false)
    );
  }

  activateDiscount(discount: Discount, organiserId?: number): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(
      this.environmentService.getApiUrl() + "/api/discounts/activate", discount.id
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value.result ?? false)
    );
  }

  deactivateDiscount(discount: Discount, organiserId?: number): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(
      this.environmentService.getApiUrl() + "/api/discounts/deactivate", discount.id
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value.result ?? false)
    );
  }

  findUsers(discount: Discount, organiserId?: number): Observable<UserDetail[]> {
    this.loaderService.startLoading();
    return this.http.get<ActionResultList<UserDetail[], number>>(
      this.environmentService.getApiUrl() + "/api/discounts/findAllUsers?discountId=" + discount.id + (organiserId ? "&organiserId=" + organiserId : "")
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  findUsersWithCoupon(discountId: number, hideLoading?: boolean, organiserId?: number): Observable<UserDetail[]> {
    if (!hideLoading) this.loaderService.startLoading();
    return this.http.get<ActionResultList<UserDetail[], number>>(
      this.environmentService.getApiUrl() + "/api/discounts/findUsersWithCoupon?discountId=" + discountId + (organiserId ? "&organiserId=" + organiserId : "")
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  findUsersWithUsedCoupon(discountId: number, hideLoading?: boolean, organiserId?: number): Observable<UserDetail[]> {
    if (!hideLoading) this.loaderService.startLoading();
    return this.http.get<ActionResultList<UserDetail[], number>>(
      this.environmentService.getApiUrl() + "/api/discounts/findUsersWithUsedCoupon?discountId=" + discountId + (organiserId ? "&organiserId=" + organiserId : "")
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  couponUserRequest(couponUserRequest: CouponUserRequest, hideLoading?: boolean): Observable<boolean> {
    if (!hideLoading) this.loaderService.startLoading();
    return this.http.post<ActionResult<boolean>>(
      this.environmentService.getApiUrl() + "/api/discounts/couponUserRequest", couponUserRequest
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value.result ?? false)
    );
  }

  findEventsForDiscount(organiserId?: number): Observable<EventItem[]> {
    this.loaderService.startLoading();
    return this.http.get<ActionResultList<EventItem[], number>>(
      this.environmentService.getApiUrl() + "/api/discounts/findEventsForDiscount" + (organiserId ? "?organiserId=" + organiserId : "")
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value?.result?.length ? value.result : [])
    );
  }

  fetchIsCouponValid(couponCode: string, raceId: number): Observable<boolean> {
    this.loaderService.startLoading();
    return this.http.get<ActionResult<boolean>>(
      this.environmentService.getApiUrl() + "/api/discounts/coupon/isValid?couponCode=" + couponCode + "&raceId=" + raceId
    ).pipe(
      tap(() => this.loaderService.dismissLoading()),
      map(value => value.result ?? false)
    );
  }
}
